import { useEffect, useState } from 'react';
import type {
  ApiBlockListModel,
  CategoryContentResponseModel,
  ProductListItemDto,
  ProductSortKey,
  ProductTagFacetDto,
  SubCategoryContentResponseModel,
} from '@/services/platform/api/model';
import { cn, mapCmsHeroSlides, useInfiniteScroll, useLocaleStore } from '@/lib/core';
import { useUIStore } from '@/lib/core/stores/useUIStore';
import type { Image } from '@/lib/core/utils/image';
import type { AppContext } from '@/lib/core/types/modules';
import { useLocation, useNavigate } from 'react-router-dom';
import { useShopFavoriteIndex } from '@/services/platform/data/shop';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { ShopHeaderActions } from '@/components/shared/shop/layout/ShopHeaderActions';
import { ShopProductListFilterSheet, type ShopProductListFilterSheetFilters } from './filters/sheet';
import { ShopProductListEmpty } from './empty';
import { ShopProductListInfo } from './info';
import { ShopProductListTabBar } from './filters/tab-bar';
import { ShopProductListHeroCarousel } from './banners/hero-carousel';
import { ShopProductListBanner } from './banners/banner';
import { ShopProductListView1Grid } from './views/view-1-grid';
import { ShopProductListView2Grid } from './views/view-2-grid';
import { ShopProductListView3Grid } from './views/view-3-grid';

export type ProductView = 1 | 2 | 3;

export type ShopProductListProps = {
  products: ProductListItemDto[];
  title?: string;
  description?: string;
  category?: CategoryContentResponseModel;
  subCategory?: SubCategoryContentResponseModel;
  bannerImage?: Image;
  heroSlideshow?: ApiBlockListModel;
  sortBy?: ProductSortKey;
  categories?: ProductTagFacetDto[] | null;
  subcategories?: ProductTagFacetDto[] | null;
  subSubcategories?: ProductTagFacetDto[] | null;
  activeTabId: string;
  onTabChange: (id: string) => void;
  activeFilterCount?: number;
  brands?: string[] | null;
  colors?: ProductTagFacetDto[] | null;
  minPrice?: number | null;
  maxPrice?: number | null;
  lockedBrand?: string;
  selectedBrands: string[];
  selectedColorSlugs: string[];
  priceRange: [number, number];
  isFilterOpen: boolean;
  onOpenFilter: () => void;
  onCloseFilter: () => void;
  onApplyFilters: (filters: ShopProductListFilterSheetFilters) => void;
  hasNextPage?: boolean;
  isLoading?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  context?: AppContext;
};

export function ShopProductList({
  products,
  title,
  description,
  category,
  subCategory,
  bannerImage,
  heroSlideshow,
  sortBy,
  categories,
  subcategories,
  subSubcategories,
  activeTabId,
  onTabChange,
  activeFilterCount,
  brands,
  colors,
  minPrice,
  maxPrice,
  lockedBrand,
  selectedBrands,
  selectedColorSlugs,
  priceRange,
  isFilterOpen,
  onOpenFilter,
  onCloseFilter,
  onApplyFilters,
  hasNextPage,
  isLoading,
  loadingMore,
  onLoadMore,
  context,
}: ShopProductListProps) {
  const locale = useLocaleStore(state => state.locale);
  const setScreenHeadOverride = useUIStore(state => state.setScreenHeadOverride);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [view, setView] = useState<ProductView>(1);
  const {
    shopFavoriteLists,
    favoriteListGuidByProductId,
    refetch: refetchFavoriteIndex,
  } = useShopFavoriteIndex();
  const sentinelRef = useInfiniteScroll(
    () => onLoadMore?.(),
    Boolean(hasNextPage) && !isLoading && !loadingMore
  );
  const heroSlides = mapCmsHeroSlides(heroSlideshow?.items);
  const showHeroCarousel = (view === 2 || view === 3) && heroSlides.length > 0;

  const handleProductClick = (publicGuid: string | undefined) => {
    if (!publicGuid) return;
    navigate(`${pathname}/product_${publicGuid}`);
  };

  useEffect(() => {
    return () => setScreenHeadOverride(null);
  }, [setScreenHeadOverride]);

  useEffect(() => {
    const nextView: ProductView = view === 3 ? 1 : ((view + 1) as ProductView);
    setScreenHeadOverride({
      title: subCategory?.name ?? category?.name ?? title,
      variant: showHeroCarousel ? 'overlay' : 'default',
      rightActions: (
        <ShopHeaderActions
          context={context}
          hideSearch
          view={view}
          onViewChange={() => setView(nextView)}
          showHeroCarousel={showHeroCarousel}
        />
      ),
    });
  }, [view, title, subCategory, category, context, setScreenHeadOverride, showHeroCarousel]);

  return (
    <div
      className={cn(
        'relative flex flex-col pb-25',
        showHeroCarousel && '-mt-[calc(60px+max(1rem,env(safe-area-inset-top)))]'
      )}
    >
      {showHeroCarousel && <ShopProductListHeroCarousel slides={heroSlides} />}
      {/* Shop List Head */}
      <div className="w-full bg-white">
        <ShopProductListInfo
          title={showHeroCarousel ? undefined : title}
          description={description}
          view={view}
        />
        {(view === 2 || view === 3) && (
          <ShopProductListTabBar
            category={category}
            subCategory={subCategory}
            categories={categories}
            subcategories={subcategories}
            subSubcategories={subSubcategories}
            activeTabId={activeTabId}
            onTabChange={onTabChange}
            onOpenFilter={onOpenFilter}
            activeFilterCount={activeFilterCount}
          />
        )}
      </div>
      <ShopProductListFilterSheet
        open={isFilterOpen}
        onOpenChange={openNext => {
          if (!openNext) onCloseFilter();
        }}
        sortBy={sortBy}
        brands={brands ?? []}
        colors={colors ?? []}
        minPrice={minPrice ?? 0}
        maxPrice={maxPrice ?? 0}
        lockedBrand={lockedBrand}
        selectedBrands={selectedBrands}
        selectedColorSlugs={selectedColorSlugs}
        priceRange={priceRange}
        onApply={onApplyFilters}
        resultCount={products.length}
        isLoadingResults={Boolean(isLoading)}
      />
      {isLoading ? (
        <div className="flex justify-center py-16">
          <SpinLoading wrapperClassName="min-h-0" spinnerClassName="h-6 w-6" />
        </div>
      ) : products.length === 0 ? (
        <ShopProductListEmpty />
      ) : (
        <>
          {view === 1 && (
            <>
              {/* View 1 */}
              <ShopProductListBanner image={bannerImage} />
              <ShopProductListView1Grid products={products} onProductClick={handleProductClick} />
            </>
          )}
          {view === 2 && (
            <>
              {/* View 2 */}
              <ShopProductListView2Grid
                products={products}
                locale={locale}
                onProductClick={handleProductClick}
                favoriteLists={shopFavoriteLists}
                favoriteListGuidByProductId={favoriteListGuidByProductId}
                refetchFavoriteIndex={refetchFavoriteIndex}
              />
            </>
          )}
          {view === 3 && (
            <>
              {/* View 3 */}
              <ShopProductListView3Grid products={products} onProductClick={handleProductClick} />
            </>
          )}
        </>
      )}
      {hasNextPage && (
        <div ref={sentinelRef} className="flex justify-center py-6">
          {loadingMore && <SpinLoading wrapperClassName="min-h-0" spinnerClassName="h-6 w-6" />}
        </div>
      )}
    </div>
  );
}
